<template>
  <div class="bg-surface-default-low">
    <!-- Loading state -->
    <main v-if="isLoading" class="flex h-screen items-center justify-center">
      <RevSpinner />
    </main>

    <!-- Error state -->
    <main
      v-else-if="chatNotFoundError"
      class="flex h-screen flex-col items-center justify-center space-y-16"
    >
      <p class="text-static-default-hi text-center">
        This conversation could not be found.
      </p>
      <RevButton :to="{ name: SHOPPING_ASSISTANT_NEW }" variant="primary">
        Create new conversation
      </RevButton>
    </main>

    <!-- Chat UI -->
    <main v-else class="flex h-screen flex-col justify-end">
      <div ref="chatContainerRef" class="overflow-y-auto">
        <RevContainer>
          <div class="flex flex-col space-y-20 pb-160 pt-60">
            <div class="flex flex-col space-y-20">
              <template v-for="(event, index) in events" :key="index">
                <div :ref="setEventRef(index)" class="flex flex-col py-10">
                  <!-- User message -->
                  <UserMessage
                    v-if="
                      event.type === 'message' && event.role === EVENT_ROLE_USER
                    "
                  >
                    {{ event.content }}
                  </UserMessage>

                  <!-- Assistant message -->
                  <!-- eslint-disable vue/no-v-html, vue/no-v-text-v-html-on-component -->
                  <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
                  <AssistantMessage
                    v-else-if="
                      event.type === 'message' &&
                      event.role === EVENT_ROLE_ASSISTANT
                    "
                    v-html="event.content"
                  />
                  <!-- eslint-enable -->

                  <!-- Product recommendations -->
                  <section
                    v-else-if="event.type === 'product_recommendation'"
                    class="flex flex-wrap gap-16"
                  >
                    <ChatProductCard
                      v-for="(product, productIndex) in event.products"
                      :key="productIndex"
                      :product
                    />
                  </section>

                  <!-- Prompt suggestions -->
                  <div v-else-if="event.type === 'prompt_suggestion'">
                    <PromptSuggestions
                      :suggestions="event.prompts"
                      @click="handleSuggestionClick"
                    />
                  </div>

                  <!-- No products available -->
                  <AssistantMessage
                    v-else-if="event.type === 'no_product_recommendation'"
                  >
                    {{ event.content }}
                  </AssistantMessage>
                </div>
              </template>
            </div>

            <div
              v-if="isProcessing"
              ref="processingRef"
              class="text-static-default-low flex items-center space-x-6"
            >
              <RevSpinner size="small" />
              <span>Thinking…</span>
            </div>
          </div>
        </RevContainer>
      </div>
    </main>

    <aside
      v-if="!isLoading && !chatNotFoundError"
      class="fixed inset-x-8 bottom-32"
    >
      <form class="flex justify-center" @submit.prevent="handleSubmit">
        <div
          class="flex w-full rounded-full bg-static-default-mid items-center h-48 p-8"
        >
          <label class="sr-only" for="text-input">{{
            i18n(translations.inputPlaceholder)
          }}</label>
          <input
            id="text-input"
            v-model="inputText"
            autocomplete="off"
            class="bg-transparent border-none w-full outline-none p-8"
            :disabled="isProcessing || !chatId"
            :placeholder="i18n(translations.inputPlaceholder)"
          />
          <RevButtonIcon
            :aria-label="i18n(translations.sendMessage)"
            :disabled="isProcessing || !chatId"
            :icon="IconArrowUp"
            size="small"
            type="submit"
            variant="primary"
          />
        </div>
      </form>
    </aside>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter, useRuntimeConfig } from '#imports'
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onMounted,
  ref,
} from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevContainer } from '@ds/components/Container'
import { RevSpinner } from '@ds/components/Spinner'
import { IconArrowUp } from '@ds/icons/IconArrowUp'

import {
  type ChatEvent,
  EVENT_ROLE_ASSISTANT,
  EVENT_ROLE_USER,
  MESSAGE_ROLE_USER,
} from '~/api/shopping-assistant/types'

import { useShoppingAssistantApi } from '../composables/useShoppingAssistantApi'
import { SHOPPING_ASSISTANT, SHOPPING_ASSISTANT_NEW } from '../routes.constants'

import translations from './ShoppingAssistant.translations'
import AssistantMessage from './components/AssistantMessage.vue'
import ChatProductCard from './components/ChatProductCard.vue'
import PromptSuggestions from './components/PromptSuggestions.vue'
import UserMessage from './components/UserMessage.vue'

const config = useRuntimeConfig()
const isLocal = config.public.ENVIRONMENT === 'local'

const logger = useLogger()
const router = useRouter()
const route = useRoute()
const i18n = useI18n()

const inputText = ref('')
const events = ref<ChatEvent[]>([])
const isLoading = ref(true)
const isProcessing = ref(false)
const chatNotFoundError = ref(false)
const eventRefs = new Map<number, HTMLElement>()
const firstNewEventIndex = ref<number | null>(null)
const processingRef = ref<HTMLElement | null>(null)
const chatId = ref<string | null>(
  typeof route.params.chatId === 'string' ? route.params.chatId : null,
)

const isNewConversation = computed(() => route.name === SHOPPING_ASSISTANT_NEW)

function setEventRef(index: number) {
  return (el: Element | ComponentPublicInstance | null) => {
    if (el) {
      eventRefs.set(index, el as HTMLElement)
    } else {
      eventRefs.delete(index)
    }
  }
}

const { startChat: apiStartChat, sendMessage: apiSendMessage } =
  useShoppingAssistantApi()

function processEvents(newEvents: ChatEvent[]) {
  firstNewEventIndex.value = events.value.length
  events.value.push(...newEvents)
}

async function startChat(existingChatId: string | null) {
  try {
    chatNotFoundError.value = false
    const { data } = await apiStartChat(existingChatId, {
      // TODO Use real user info if available (or move it to the service)
      context: 'Front Office User',

      by: isLocal ? 'tester' : 'user',
      resume_latest: !isNewConversation.value,
    })

    if (!existingChatId) {
      // Update URL without triggering Vue Router navigation (avoids remount)
      // Per Vue Router docs: preserve history.state when using replaceState
      const newPath = router.resolve({
        name: SHOPPING_ASSISTANT,
        params: {
          chatId: data.id,
        },
      }).href

      window.history.replaceState(window.history.state, '', newPath)

      chatId.value = data.id
    }

    processEvents(data.events)
  } catch (error) {
    const err = error as { status?: number }
    if (err.status === 404) {
      chatNotFoundError.value = true
    } else {
      logger.error('[SHOPPING_ASSISTANT] Failed to create chat', {
        error: error as Error,
        owners: ['bot-group-ai-shopping-assistant-front'],
      })
    }
  } finally {
    isLoading.value = false
    scrollToLastMessage()
  }
}

async function sendMessage(content: string): Promise<void> {
  if (!chatId.value) return

  try {
    isProcessing.value = true

    // Add user message as a local event
    events.value.push({
      type: 'message',
      role: EVENT_ROLE_USER,
      content,
    })

    scrollToProcessing()

    const data = await apiSendMessage(chatId.value, {
      content,
      role: MESSAGE_ROLE_USER,
    })

    processEvents(data.events)
    scrollToFirstNewMessage()
  } catch (error) {
    logger.error('[SHOPPING_ASSISTANT] Failed to send message', {
      error: error as Error,
      owners: ['bot-group-ai-shopping-assistant-front'],
    })
    events.value.push({
      type: 'message',
      role: EVENT_ROLE_ASSISTANT,
      content: 'Failed to send message. Please try again.',
    })
  } finally {
    isProcessing.value = false
  }
}

async function scrollToLastMessage() {
  await nextTick()
  const lastIndex = events.value.length - 1
  eventRefs
    .get(lastIndex)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

async function scrollToProcessing() {
  await nextTick()
  processingRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

async function scrollToFirstNewMessage() {
  await nextTick()
  if (firstNewEventIndex.value !== null) {
    eventRefs
      .get(firstNewEventIndex.value)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Event Handlers
async function handleSubmit() {
  const content = inputText.value.trim()
  if (!content || !chatId.value || isProcessing.value) return

  inputText.value = ''
  await sendMessage(content)
}

async function handleSuggestionClick(text: string) {
  // Remove all prompt suggestions from the events
  events.value = events.value.filter(
    (event) => event.type !== 'prompt_suggestion',
  )

  inputText.value = text
  await handleSubmit()
}

// Initialization
onMounted(() => {
  startChat(isNewConversation.value ? null : chatId.value)
})
</script>
